/**
 * Admin toast / flash / notification-WS transport.
 *
 * Phase 1.3 of the `frontend/admin/admin.js` decomposition. Owns:
 *
 *   - The `#flash` banner ("flash" — short transient one-line messages).
 *   - The toast tray (`#toastLayer` + `#notificationTray`) that slides in
 *     longer-lived notifications.
 *   - The `/ws/notifications` WebSocket lifecycle: open / reconnect / close,
 *     gated by an injected `isAuthorized()` predicate so the socket isn't
 *     opened until the user has a valid session.
 *
 * Boot contract: `admin.html` loads this script before `admin.js`. Generic
 * SPA-domain logic — message dispatch, ticket dedup, etc. — stays in
 * `admin.js` and is wired in via `adminNotifications.configure({...})` at
 * boot. The exported surface is:
 *
 *     window.adminNotifications = {
 *       configure({ isAuthorized, onMessage }),
 *       showFlash(msg, type, timeout),
 *       ensureToastLayer(),
 *       pushToast(options),
 *       dismissToast(id, options),
 *       ensureSocket(),
 *       closeSocket(force),
 *     };
 *
 * Phase 3.2.a: dropped the IIFE wrapper; module is now ESM (strict by
 * default). Public surface exported as named exports + still attached to
 * `window.adminNotifications` for view modules that haven't migrated yet.
 */

// ---- Cross-tab WS registry --------------------------------------------
  // The registry persists in `window` so this module can be re-evaluated
  // (e.g. on hot-reload) without leaking sockets. The legacy key matches the
  // pre-extraction value so existing browser sessions keep their connection.
  const REGISTRY_KEY = '__inetNotificationSocketRegistry__';
  const registry = window[REGISTRY_KEY]
    || (window[REGISTRY_KEY] = { socket: null, shouldReconnect: true });

  // ---- WS state ---------------------------------------------------------
  const RECONNECT_DELAY_MS = 5000;
  let socket = registry.socket || null;
  let reconnectTimer = null;
  let shouldReconnect = typeof registry.shouldReconnect === 'boolean'
    ? registry.shouldReconnect
    : true;

  // ---- Toast state ------------------------------------------------------
  // DOM refs are resolved lazily — the script loads in <head>/early in <body>
  // so the elements may not exist yet at IIFE-evaluation time.
  let flashEl = null;
  let toastLayer = null;
  let notificationTray = null;
  function resolveDom() {
    if (!flashEl) flashEl = document.getElementById('flash');
    if (!toastLayer) toastLayer = document.getElementById('toastLayer');
    if (!notificationTray) notificationTray = document.getElementById('notificationTray');
  }

  const toastEntries = new Map();
  let toastSequence = 0;

  // ---- Injected hooks (defaults are safe no-ops for isolated tests) -----
  let isAuthorized = () => true;
  let onMessage = () => {};

  function configure(options) {
    if (!options) return;
    if (typeof options.isAuthorized === 'function') isAuthorized = options.isAuthorized;
    if (typeof options.onMessage === 'function') onMessage = options.onMessage;
  }

  // ---- Flash banner -----------------------------------------------------
  function ensureToastLayer() {
    resolveDom();
    if (!toastLayer) return;
    try {
      if (toastLayer.open && typeof toastLayer.close === 'function') {
        toastLayer.close();
      }
      if (typeof toastLayer.show === 'function') {
        toastLayer.show();
      } else {
        toastLayer.open = true;
      }
    } catch {
      toastLayer.open = true;
    }
  }

  function showFlash(msg, type = 'success', timeout = 30000) {
    resolveDom();
    ensureToastLayer();
    if (!flashEl) return;
    flashEl.textContent = msg;
    flashEl.className = `flash show ${type === 'error' ? 'error' : ''}`;
    if (timeout) {
      setTimeout(() => { flashEl.className = 'flash'; }, timeout);
    }
  }

  // ---- Toast tray -------------------------------------------------------
  function dismissToast(id, { immediate = false } = {}) {
    if (!id) return;
    const entry = toastEntries.get(id);
    if (!entry) return;
    toastEntries.delete(id);
    if (entry.timer) {
      clearTimeout(entry.timer);
    }
    const toastEl = entry.element;
    if (!toastEl) return;
    const remove = () => {
      if (toastEl.parentElement) {
        toastEl.parentElement.removeChild(toastEl);
      }
    };
    if (immediate) {
      remove();
      return;
    }
    toastEl.classList.remove('show');
    toastEl.addEventListener('transitionend', remove, { once: true });
    setTimeout(remove, 350);
  }

  function pushToast({
    id,
    title,
    message,
    icon = '🔔',
    kind = 'info',
    autoCloseMs = 30000,
    onClick,
    data = {},
  }) {
    resolveDom();
    const toastId = id || `toast-${++toastSequence}`;
    if (!notificationTray) {
      if (message) showFlash(message);
      return toastId;
    }
    ensureToastLayer();
    if (toastEntries.has(toastId)) {
      dismissToast(toastId, { immediate: true });
    }
    const toastEl = document.createElement('div');
    toastEl.className = `notification-toast ${kind}`;
    toastEl.setAttribute('role', 'status');

    if (data && Object.prototype.hasOwnProperty.call(data, 'ticketId')) {
      toastEl.dataset.ticketId = String(data.ticketId);
    }

    const iconEl = document.createElement('div');
    iconEl.className = 'toast-icon';
    iconEl.textContent = icon;

    const contentEl = document.createElement('div');
    contentEl.className = 'toast-content';

    const titleEl = document.createElement('div');
    titleEl.className = 'toast-title';
    titleEl.textContent = title || 'Update';
    contentEl.appendChild(titleEl);

    if (message) {
      const messageEl = document.createElement('p');
      messageEl.className = 'toast-message';
      messageEl.textContent = message;
      contentEl.appendChild(messageEl);
    }

    const closeBtn = document.createElement('button');
    closeBtn.className = 'toast-close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Dismiss notification');
    closeBtn.textContent = '×';

    closeBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      dismissToast(toastId);
    });

    toastEl.addEventListener('click', () => {
      try {
        if (typeof onClick === 'function') {
          onClick();
        }
      } finally {
        dismissToast(toastId);
      }
    });

    toastEl.appendChild(iconEl);
    toastEl.appendChild(contentEl);
    toastEl.appendChild(closeBtn);
    notificationTray.appendChild(toastEl);

    requestAnimationFrame(() => {
      toastEl.classList.add('show');
    });

    let timer = null;
    if (autoCloseMs && autoCloseMs > 0) {
      timer = setTimeout(() => dismissToast(toastId), autoCloseMs);
    }
    toastEntries.set(toastId, { element: toastEl, timer });
    return toastId;
  }

  // ---- WebSocket lifecycle ---------------------------------------------
  // Defensive: drop a stale socket from a previous page-script load.
  if (socket && ![WebSocket.OPEN, WebSocket.CONNECTING].includes(socket.readyState)) {
    socket = null;
    registry.socket = null;
  }

  function bindSocket(s) {
    if (!s) return;
    socket = s;
    registry.socket = s;
    s.onopen = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };
    s.onmessage = (event) => {
      try { onMessage(event); } catch {}
    };
    s.onclose = () => {
      if (socket === s) {
        socket = null;
        registry.socket = null;
      }
      if (shouldReconnect) {
        scheduleReconnect();
      }
    };
    s.onerror = () => {
      try { s.close(); } catch {}
    };
  }

  function ensureSocket() {
    if (!isAuthorized()) {
      closeSocket(true);
      return;
    }
    shouldReconnect = true;
    registry.shouldReconnect = true;
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (!socket && registry.socket) {
      const existing = registry.socket;
      if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
        bindSocket(existing);
        return;
      }
      registry.socket = null;
    }
    connectSocket();
  }

  function connectSocket() {
    try {
      if (socket) {
        try { socket.close(); } catch {}
      }
      const scheme = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
      // Browsers attach same-origin cookies to WS handshakes automatically,
      // so the admin_token cookie carries auth without exposing the token in
      // the URL (which would leak into proxy/access logs).
      const url = `${scheme}${window.location.host}/ws/notifications`;
      const newSocket = new WebSocket(url);
      bindSocket(newSocket);
    } catch {
      socket = null;
      registry.socket = null;
      scheduleReconnect();
    }
  }

  function closeSocket(force = false) {
    if (force) {
      shouldReconnect = false;
      registry.shouldReconnect = false;
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket) {
      try { socket.close(); } catch {}
    }
    socket = null;
    registry.socket = null;
  }

  function scheduleReconnect() {
    if (!shouldReconnect) return;
    if (reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      ensureSocket();
    }, RECONNECT_DELAY_MS);
  }

  // ---- Flash auto-dismiss on user interaction --------------------------
  // Matches the pre-extraction behaviour: any click/keystroke clears a
  // visible flash so the user isn't blocked by a stale toast.
  function attachFlashDismissHandlers() {
    const dismiss = () => {
      resolveDom();
      if (flashEl) flashEl.className = 'flash';
    };
    ['click', 'input', 'change', 'keydown'].forEach((evt) => {
      document.addEventListener(evt, dismiss, { capture: true });
    });
  }
  // The script can run before document is interactive; wait if needed.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachFlashDismissHandlers, { once: true });
  } else {
    attachFlashDismissHandlers();
  }

// Phase 3.2.e: every caller now uses `import` directly. The
// `window.adminNotifications` global no longer exists.

export {
  configure,
  showFlash,
  ensureToastLayer,
  pushToast,
  dismissToast,
  ensureSocket,
  closeSocket,
};
